import { Link } from "react-router-dom";
import { BadgeCheck, Check, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import useSEO from "../hooks/useSEO";

const INCLUSI_CARICO = [
  "Denti fissi in 6 ore, se le condizioni lo consentono",
  "4 impianti certificati",
  "MUA e componentistica",
  "Protesi fissa avvitata provvisoria",
  "Protesi definitiva avvitata in composito medicale con barra di rinforzo",
  "TAC 3D e pianificazione digitale in sede",
  "Interventi seguiti dal Dott. Gattuso, 25 anni di esperienza",
];

const INCLUSI_OVERDENTURE = [
  "Due impianti certificati",
  "Componentistica",
  "Protesi su barra e con attacchi in resina",
  "Composito rimovibile",
];

const ALTRI_TRATTAMENTI = [
  { t: "Protesi fisse", d: "In zirconio-ceramica o lega-ceramica, su impianti o denti naturali." },
  { t: "Ortodonzia invisibile", d: "Allineatori Invisalign® e brackets, per bambini, adolescenti e adulti." },
  { t: "Parodontologia e igiene", d: "Trattamento della parodontite, pulizia professionale e mantenimento." },
];

export default function Prezzi() {
  useSEO({
    title: "Prezzi | Dentalchivasso — Implantologia a carico immediato e protesi a Chivasso",
    description:
      "Prezzi trasparenti Dentalchivasso: implantologia a carico immediato € 5.900, overdenture su 2 impianti da € 3.900. Preventivo personalizzato dopo la prima visita con TAC 3D a Chivasso.",
    path: "/prezzi",
  });

  return (
    <div data-testid="prezzi-page">
      <div className="bg-ivory">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-32 pb-16 md:pt-40 md:pb-24">
          <Reveal>
            <Eyebrow>Prezzi trasparenti</Eyebrow>
            <h1 className="mt-5 font-display font-medium tracking-tight text-ink text-4xl md:text-5xl leading-[1.08] max-w-3xl">
              Chiarezza sul costo, <span className="italic text-teal">prima di iniziare.</span>
            </h1>
            <p className="mt-6 text-mutedc leading-relaxed max-w-2xl">
              Ecco le voci di costo principali dello studio. Ogni preventivo è personalizzato e
              confermato dopo la prima visita con TAC: nessun costo nascosto.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="bg-ink text-ivory relative overflow-hidden grain">
        <div className="absolute -bottom-40 -right-32 w-[480px] h-[480px] rounded-full bg-teal/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl">
            <Reveal>
              <div className="rounded-3xl border border-teal/40 bg-teal/10 p-9 h-full" data-testid="prezzi-card-carico">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-mist">Il più richiesto</p>
                <p className="mt-5 font-display text-5xl font-semibold">€ 5.900</p>
                <p className="mt-2 font-semibold">Implantologia a carico immediato · All-on-Four</p>
                <ul className="mt-5 space-y-2.5">
                  {INCLUSI_CARICO.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-sm text-ivory/75">
                      <Check size={15} className="text-teal-mist mt-0.5 shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-xs text-ivory/55 leading-relaxed">
                  Con un piccolo sovraprezzo è possibile richiedere la protesi su impianti definitiva
                  in ceramica nano ibrida.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-ivory/15 bg-ivory/5 p-9 h-full" data-testid="prezzi-card-overdenture">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">Protesi rimovibile</p>
                <p className="mt-5 font-display text-5xl font-semibold">da € 3.900</p>
                <p className="mt-2 font-semibold">Overdenture su 2 impianti</p>
                <ul className="mt-5 space-y-2.5">
                  {INCLUSI_OVERDENTURE.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-sm text-ivory/75">
                      <Check size={15} className="text-teal-mist mt-0.5 shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-xs text-ivory/55 leading-relaxed">
                  La soluzione stabile per chi vuole dire addio alle protesi mobili classiche.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="md:col-span-2">
              <div
                className="rounded-3xl border border-gold/40 bg-gold/10 p-9 md:p-10 md:grid md:grid-cols-[auto,1fr] md:gap-10 md:items-center h-full"
                data-testid="prezzi-card-faccette"
              >
                <div className="shrink-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">Estetica del sorriso</p>
                  <p className="mt-3 font-display text-4xl md:text-5xl font-semibold">
                    € 550 <span className="text-lg md:text-xl font-normal text-ivory/60">a faccetta</span>
                  </p>
                </div>
                <p className="mt-5 md:mt-0 md:border-l md:border-ivory/15 md:pl-10 text-sm md:text-base text-ivory/70 leading-relaxed">
                  Faccetta estetica in ceramica, provvisorio incluso. Il tuo nuovo sorriso senza
                  trapano, con un prezzo chiaro per ogni dente.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <h2 className="mt-20 font-display font-medium tracking-tight text-3xl md:text-4xl">
              Altri trattamenti, <span className="italic text-teal-mist">preventivo su misura.</span>
            </h2>
          </Reveal>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ALTRI_TRATTAMENTI.map((t, i) => (
              <Reveal key={t.t} delay={0.08 * i} className="h-full">
                <div className="rounded-3xl border border-ivory/15 bg-ivory/5 p-7 h-full" data-testid={`prezzi-altro-${i}`}>
                  <p className="font-semibold">{t.t}</p>
                  <p className="mt-2 text-sm text-ivory/65 leading-relaxed">{t.d}</p>
                  <p className="mt-4 text-xs font-semibold text-teal-mist">Preventivo dopo la prima visita</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <p className="mt-12 text-sm text-ivory/50 flex items-start gap-2.5 max-w-3xl" data-testid="prezzi-note">
              <BadgeCheck size={16} className="text-gold shrink-0 mt-0.5" />
              Ogni preventivo è personalizzato e confermato dopo la prima visita con TAC. Impianti
              100% italiani, certificati e tracciabili. Ogni situazione è diversa: solo una visita
              specialistica può stabilire il percorso più indicato.
            </p>
            <p
              className="mt-4 text-xs text-ivory/40 leading-relaxed max-w-3xl"
              data-testid="prezzi-legal-note"
            >
              Comunicazione conforme alle norme vigenti sulla pubblicità sanitaria: i listini sono
              visibili in sede.
            </p>
          </Reveal>

          <Reveal delay={0.25}>
            <Link
              to="/contatti"
              data-testid="prezzi-cta"
              className="group mt-10 inline-flex items-center gap-2 rounded-full bg-teal text-ivory font-semibold text-sm px-7 py-4 hover:bg-teal-deep transition-colors duration-300"
            >
              Prenota una visita
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
